<script setup lang="ts">
import {isEmpty} from "lodash";
import {ChevronRightIcon} from "lucide-vue-next";
import {computed, onMounted,type Ref, ref, watch} from "vue";
import Input from "../../../backend/ui/Input.vue";
import type {AddressResponse} from "../../../responses/AddressResponse";
import type {ErrorBagResponse} from "../../../responses/ErrorBagResponse";
import type {OldResponse} from "../../../responses/OldResponse";
import {label} from "../../mixins/translations.js";
import {useAddressStore} from "../../stores/address";
import Errors from "../Errors.vue";

interface Props {
    names?: { [key: string]: string },
    type?: string,
    countries: { [key: string]: string },
    defaultCountry?: string,
    splitAddressCountries?: string[],
    errors?: ErrorBagResponse,
    showInputs?: boolean,
    showAddressInputs?: boolean,
    hidePreview?: boolean,
    salutation_options?: string[]
    modelValue?: AddressResponse,
    old?: OldResponse,
}

const props = withDefaults(defineProps<Props>(), {
    names: { //field names, important for use in blade forms
        id: "id",
        company: "company",
        salutation: "salutation",
        first_name: "first_name",
        insertion: "insertion",
        last_name: "last_name",
        postal: "postal",
        house_number: "house_number",
        house_number_addition: "house_number_addition",
        street: "street",
        city: "city",
        country: "country",
        address_line_1: 'address_line_1',
        address_line_2: 'address_line_2',
        type: 'type',
    },
    defaultCountry: 'NL',
    splitAddressCountries: [
        'NL',
        'BE'
    ],
    errors: {},
    showInputs: false,
    showAddressInputs: false,
    hidePreview: false,
    salutation_options: [
        'male',
        'female',
        'other'
    ],
    type: 'delivery',
    modelValue: {},
    old: {}
})

const emit = defineEmits<(e: 'update:modelValue', value: object | null) => void>();

const addressStore = useAddressStore()

const minCharacters = 5;
const showSearchBar: boolean = import.meta.env.VITE_HAS_PLACES === 'true';

const address: Ref<AddressResponse> = ref(props.modelValue);
const addressPrefilled: Ref<boolean> = ref(false);
const showPreview: Ref<boolean> = ref(false); //only if has a preset adres
const showInputs: Ref<boolean> = ref(false);
const showAddressInputs: Ref<boolean> = ref(false);
const search: Ref<string> = ref('')

const detailResult: Ref<AddressResponse> = ref({});
const searchResults: Ref<object[]> = ref([])


const hasSplitAddress = computed(() => {
    return props.splitAddressCountries.includes(address.value.country);
})

const searchAddresses = async () => {
    if (search.value.length > minCharacters) {
        searchResults.value = await addressStore.getSearchResults(search.value)
    } else {
        searchResults.value = [];
    }
}
const chooseAddress = async (place_id) => {
    detailResult.value = await addressStore.getAddressDetails(place_id, props.splitAddressCountries);
    search.value = '';
    searchResults.value = [];
}

const salutation = computed(()=> {
    return label('site.account.salutation.'+address.value.salutation);
})


watch(detailResult, async (newValue, oldValue) => {
    if (newValue.postal){ //met omweg om NAW gegevens niet te overschrijven
        address.value = Object.assign({}, address.value, newValue); //object merge
    }
})

watch(address, async (newValue, oldValue) => {
    //if (newValue.postal){
        showInputs.value = true;
        showAddressInputs.value = true;
        emit('update:modelValue', address.value);
  //  }
})

onMounted(() => {
    addressPrefilled.value = Object.keys(address.value).length !== 0;
    showPreview.value = (addressPrefilled.value && !props.hidePreview && isEmpty(props.errors) ); //only if has a preset address
    showInputs.value = (addressPrefilled.value || props.showInputs || !showSearchBar || !isEmpty(props.errors));
    showAddressInputs.value = (addressPrefilled.value || props.showAddressInputs || !showSearchBar || !isEmpty(props.errors));

    if(!addressPrefilled) {
        address.value = { country: props.defaultCountry, type: props.type };
    }
    address.value = { ...address.value , ...props.old };
})

</script>
<template>
    <div>
        <!-- Preset value-->
        <div v-if="showPreview">
            <div class="border font-semibold rounded-md p-4 min-h-fit">
                <div class="w-full">{{ address.company }}</div>
<!--                <div class="w-full">-->
<!--                    <span v-html="salutation"></span> {{ address.first_name }}-->
<!--                    {{ address.insertion }} {{ address.last_name }}-->
<!--                </div>-->
                <div class="w-full">{{ address.address_line_1 }}</div>
                <div class="w-full">{{ address.address_line_2 }}</div>
                <div class="w-full">{{ address.street }} {{ address.house_number }}
                    {{ address.house_number_addition }}
                </div>
                <div class="w-full">{{ address.postal }} {{ address.city }}</div>
                <div class="w-full">{{ countries[address.country] ?? '' }}</div>
                <div class="relative cursor-pointer text-right text-sm text-gray-600" @click="showPreview = false">
                    {{ label('site.address.edit') }}
                </div>
            </div>
            <input type="hidden" :name="names.id" v-model="address.id"/>
            <input type="hidden" :name="names.type" v-model="address.type"/>
            <input type="hidden" :name="names.company" v-model="address.company"/>
            <input type="hidden" :name="names.salutation" v-model="address.salutation"/>
            <input type="hidden" :name="names.first_name" v-model="address.first_name"/>
            <input type="hidden" :name="names.insertion" v-model="address.insertion"/>
            <input type="hidden" :name="names.last_name" v-model="address.last_name"/>
            <input type="hidden" :name="names.street" v-model="address.street"/>
            <input type="hidden" :name="names.address_line_1" v-model="address.address_line_1"/>
            <input type="hidden" :name="names.address_line_2" v-model="address.address_line_2"/>
            <input type="hidden" :name="names.house_number" v-model="address.house_number"/>
            <input type="hidden" :name="names.house_number_addition" v-model="address.house_number_addition"/>
            <input type="hidden" :name="names.postal" v-model="address.postal"/>
            <input type="hidden" :name="names.city" v-model="address.city"/>
            <input type="hidden" :name="names.country" v-model="address.country"/>
        </div>
        <div v-else>
            <!-- Input fields-->
            <div v-if="showInputs">
                <input type="hidden" :name="names.id" v-model="address.id"/>
                <div class="w-full mb-2">
                    <div class="flex justify-between">
                        <label :for="names.company" class="mb-2 block text-sm font-semibold text-slate-700">
                            {{ label('site.account.company') }}
                        </label>
                    </div>
                    <input
                        type="text"
                        :name="names.company"
                        :id="names.company"
                        v-model="address.company"
                        :placeholder="label('site.if_applicable')"
                        autocomplete="off"
                        class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                    />
                    <Errors :errors="errors[names.company]"/>
                </div>
<!--                <div class="w-full mb-2">-->
<!--                    <div class="flex justify-between">-->
<!--                        <label :for="names.salutation" class="mb-2 block text-sm font-semibold text-slate-700">-->
<!--                            {{ label('site.account.salutation') }}-->
<!--                            <span class="text-error-500" :title="label('required')">*</span>-->
<!--                        </label>-->
<!--                    </div>-->
<!--                    <div v-for="salutation in salutation_options"-->
<!--                         class="flex cursor-pointer items-center rounded-full focus:outline-hidden ring-blue-400 checked:bg-primary-500 gap-x-2 mt-2">-->
<!--                        <input-->
<!--                            type="radio"-->
<!--                            :id="names.salutation+'_'+salutation"-->
<!--                            :name="names.salutation"-->
<!--                            :value="salutation"-->
<!--                            v-model="address.salutation"-->
<!--                            class="checked:bg-primary-500 border-secondary-300 outline-primary-500 checked:hover:bg-primary-500 checked:focus:bg-primary-500 focus:bg-primary-500 focus:outline-primary-500" />-->
<!--                        <label :for="names.salutation+'_'+salutation" class="mb-2 block text-sm font-semibold text-slate-700 cursor-pointer">{{-->
<!--                                label('site.account.salutation_options.' + salutation)-->
<!--                            }} </label>-->
<!--                    </div>-->
<!--                    <Errors :errors="errors[names.salutation]"/>-->
<!--                </div>-->

                <div class="grid grid-cols-3 gap-4 mb-2">
                    <div>
                        <div class="flex justify-between">
                            <label :for="names.first_name" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.account.first_name') }}
                                <span class="text-error-500" :title="label('required')">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.first_name"
                            :id="names.first_name"
                            v-model="address.first_name"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.first_name]"/>
                    </div>
                    <div>
                        <div class="flex justify-between">
                            <label :for="names.insertion" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.account.insertion') }}
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.insertion"
                            :id="names.insertion"
                            v-model="address.insertion"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.insertion]"/>
                    </div>
                    <div>
                        <div class="flex justify-between">
                            <label :for="names.last_name" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.account.last_name') }}
                                <span class="text-error-500" :title="label('required')">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.last_name"
                            :id="names.last_name"
                            v-model="address.last_name"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.last_name]"/>
                    </div>
                </div>
                <slot name="naw"></slot>
            </div>
            <!-- Seachblock-->
            <div class="w-full mb-2" v-if="showSearchBar">
                <div class="flex justify-between">
                    <label for="search" class="mb-2 block text-sm font-semibold text-slate-700">
                        {{ label('site.address.search') }}
                    </label>
                </div>
                <input id="search" :placeholder="label('site.address.search')" type="text" v-model="search"
                       @keyup="searchAddresses"
                       class="w-full px-3 py-2 placeholder-gray-300 border border-secondary-300 rounded-md focus:outline-hidden focus:ring-3 focus:ring-primary-100 focus:border-primary-300"/>
                <ul class="text-sm text-secondary-700 bg-background divide-y divide-secondary-100 rounded-lg shadow-sm">
                    <li class="block px-4 py-3 hover:bg-secondary-100 font-bold w-full flex items-center justify-between cursor-pointer"
                        v-for="result in searchResults" @click="chooseAddress(result.place_id)">
                        {{ result.description }}
                        <ChevronRightIcon class="w-4 h-4"/>
                    </li>
                    <li v-if="!searchResults?.length && search.length > minCharacters"
                        class="block px-4 py-3 hover:bg-secondary-100 font-bold w-full flex items-center justify-between">
                        {{ label('site.address.no_results') }}
                    </li>
                    <li v-if="searchResults?.length">
                        <span class="flex w-full bg-secondary-100 px-3 py-3">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 116 15" aria-label="Powered by Google" role="image"
                                 class="text-secondary-400" fill="currentColor" style="max-height: 17px;">
                                <path
                                    d="M4.025 3.572c1.612 0 2.655 1.283 2.655 3.27 0 1.974-1.05 3.27-2.655 3.27-.902 0-1.63-.393-1.974-1.06h-.09v3.057H.95V3.68h.96v1.054h.094c.404-.726 1.16-1.166 2.02-1.166zm-.24 5.63c1.16 0 1.852-.884 1.852-2.36 0-1.477-.692-2.362-1.846-2.362-1.14 0-1.86.91-1.86 2.362 0 1.447.72 2.36 1.858 2.36zm7.072.91c-1.798 0-2.912-1.243-2.912-3.27 0-2.033 1.114-3.27 2.912-3.27 1.8 0 2.913 1.237 2.913 3.27 0 2.027-1.114 3.27-2.913 3.27m0-.91c1.196 0 1.87-.866 1.87-2.36 0-1.5-.674-2.362-1.87-2.362-1.195 0-1.87.862-1.87 2.362 0 1.494.675 2.36 1.87 2.36m12.206-5.518H22.05l-1.244 5.05h-.094L19.3 3.684h-.966l-1.412 5.05h-.094l-1.242-5.05h-1.02L16.336 10h1.02l1.406-4.887h.093L20.268 10h1.025zm3.632.78c-1.008 0-1.71.737-1.787 1.856h3.48c-.023-1.12-.69-1.857-1.693-1.857zm1.664 3.9h1.004c-.305 1.085-1.277 1.747-2.66 1.747-1.752 0-2.848-1.26-2.848-3.26 0-1.986 1.113-3.275 2.847-3.275 1.705 0 2.742 1.213 2.742 3.176v.387h-4.542v.047c.053 1.248.75 2.04 1.822 2.04.815 0 1.366-.3 1.63-.857zM31.03 10h1.01V6.086c0-.89.696-1.535 1.657-1.535.2 0 .563.04.645.06V3.6c-.13-.018-.34-.03-.504-.03-.838 0-1.565.434-1.752 1.05h-.094v-.938h-.96V10zm6.915-5.537c-1.008 0-1.71.738-1.787 1.857h3.48c-.023-1.12-.69-1.857-1.693-1.857m1.664 3.902h1.004c-.304 1.084-1.277 1.746-2.66 1.746-1.752 0-2.848-1.26-2.848-3.26 0-1.986 1.113-3.275 2.847-3.275 1.705 0 2.742 1.213 2.742 3.176v.387h-4.542v.047c.053 1.248.75 2.04 1.822 2.04.815 0 1.366-.3 1.63-.857zm5.01 1.746c-1.62 0-2.658-1.28-2.658-3.265 0-1.98 1.05-3.27 2.654-3.27.878 0 1.622.416 1.98 1.108h.087V1.177h1.008V10h-.96V8.992h-.093c-.4.703-1.15 1.12-2.02 1.12zm.23-5.63c-1.15 0-1.845.89-1.845 2.365s.69 2.36 1.846 2.36c1.15 0 1.858-.9 1.858-2.36 0-1.447-.715-2.362-1.857-2.362zm7.876-3.114h1.024V4.49q.346-.45.827-.69.48-.239 1.043-.237 1.172.001 1.9.806c.49.536.73 1.33.73 2.375q0 1.489-.72 2.473t-1.997.984q-.715 0-1.207-.344-.293-.206-.627-.657v.8h-.97V1.364zm4.02 7.225q.426-.679.426-1.793 0-.99-.425-1.64t-1.25-.65q-.719 0-1.264.532-.541.532-.542 1.757.001.885.224 1.435.416 1.04 1.553 1.04.856 0 1.28-.68zM63.4 3.727h1.167q-.222.603-.99 2.754a145 145 0 0 1-.96 2.635q-.915 2.402-1.29 2.93-.375.526-1.29.527-.221 0-.342-.017a3 3 0 0 1-.296-.065v-.96q.273.074.4.093.119.018.214.018.292 0 .43-.093a.9.9 0 0 0 .232-.237q.03-.05.21-.48.183-.436.264-.646l-2.32-6.457h1.196l1.68 5.11 1.694-5.11zm10.594 1.556V6.87h3.814c-.117.89-.416 1.54-.87 1.998-.557.555-1.427 1.16-2.944 1.16-2.35 0-4.184-1.882-4.184-4.217 0-2.33 1.835-4.214 4.184-4.214a4.07 4.07 0 0 1 2.873 1.135l1.122-1.116C77.04.697 75.77 0 73.99 0c-3.218 0-5.923 2.606-5.923 5.805 0 3.2 2.705 5.804 5.923 5.804 1.738 0 3.048-.57 4.073-1.63 1.05-1.044 1.382-2.52 1.382-3.71 0-.365-.028-.707-.087-.99h-5.37zm10.222-1.29c-2.082 0-3.78 1.574-3.78 3.748 0 2.154 1.698 3.747 3.78 3.747S87.998 9.9 87.998 7.74c0-2.174-1.7-3.748-3.782-3.748zm0 6.018c-1.14 0-2.127-.935-2.127-2.27 0-1.348.982-2.27 2.123-2.27s2.128.922 2.128 2.27c0 1.335-.985 2.27-2.127 2.27zm18.54-5.18h-.06c-.37-.438-1.083-.838-1.985-.838-1.88 0-3.52 1.632-3.52 3.748 0 2.102 1.64 3.747 3.52 3.747.906 0 1.62-.4 1.99-.852h.06v.523c0 1.432-.774 2.2-2.013 2.2-1.012 0-1.64-.723-1.9-1.336l-1.44.593c.414.994 1.51 2.213 3.34 2.213 1.94 0 3.58-1.135 3.58-3.902v-6.74h-1.57v.645zm-1.9 5.18c-1.144 0-2.013-.968-2.013-2.27 0-1.323.87-2.27 2.01-2.27 1.13 0 2.012.967 2.012 2.282.006 1.31-.882 2.258-2.01 2.258zM92.65 3.992c-2.084 0-3.783 1.574-3.783 3.748 0 2.154 1.7 3.747 3.782 3.747 2.08 0 3.78-1.587 3.78-3.747 0-2.174-1.7-3.748-3.78-3.748zm0 6.018c-1.143 0-2.13-.935-2.13-2.27 0-1.348.987-2.27 2.13-2.27 1.14 0 2.126.922 2.126 2.27 0 1.335-.986 2.27-2.127 2.27zM105.622.155h1.628v11.332h-1.628m6.655-1.477c-.843 0-1.44-.38-1.83-1.135l5.04-2.07-.168-.426c-.315-.84-1.275-2.39-3.228-2.39-1.94 0-3.554 1.515-3.554 3.75 0 2.1 1.595 3.744 3.736 3.744 1.725 0 2.724-1.05 3.14-1.658l-1.285-.852c-.428.62-1.01 1.032-1.855 1.032zm-.117-4.612c.668 0 1.24.342 1.427.826l-3.405 1.4c0-1.574 1.122-2.226 1.978-2.226"></path>
                            </svg>
                        </span>
                    </li>
                </ul>
                <div v-if="!showAddressInputs" class="cursor-pointer text-right mb-2 block text-sm font-semibold text-slate-700"
                     @click="showAddressInputs = true">{{ label('site.address.manual') }}
                </div>
            </div>
            <!-- Address inputs-->
            <div v-if="showAddressInputs">
                <div class="mb-2">
                    <div class="flex justify-between">
                        <label :for="names.country" class="mb-2 block text-sm font-semibold text-slate-700">
                            {{ label('site.address.country') }}
                        </label>
                    </div>
                    <select
                        :name="names.country"
                        :id="names.country"
                        v-model="address.country"
                        class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10">
                        <option v-for="(label, value) in countries" :value="value" v-text="label"></option>
                    </select>
                    <Errors :errors="errors[names.country]"/>
                </div>

                <div v-if="hasSplitAddress" class="mb-2 flex gap-4">
                    <div class="w-2/4">
                        <div class="flex justify-between">
                            <label :for="names.street" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.street') }} <span class="text-red-400">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.street"
                            :id="names.street"
                            v-model="address.street"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.street]"/>
                    </div>

                    <div class="w-1/4">
                        <div class="flex justify-between">
                            <label :for="names.house_number" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.house_number') }} <span class="text-red-400">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            min="1"
                            :name="names.house_number"
                            :id="names.house_number"
                            v-model="address.house_number"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.house_number]"/>
                    </div>
                    <div class="w-1/4">
                        <div class="flex justify-between">
                            <label :for="names.house_number_addition" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.house_number_addition') }}
                            </label>
                        </div>

                        <input
                            type="text"
                            :name="names.house_number_addition"
                            :id="names.house_number_addition"
                            v-model="address.house_number_addition"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.house_number_addition]"/>
                    </div>
                </div>
                <div v-else class="mb-2">
                    <div class="w-full lg:mb-2">
                        <div class="flex justify-between">
                            <label :for="names.address_line_1" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.address_line_1') }} <span class="text-red-400">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.address_line_1"
                            :id="names.address_line_1"
                            v-model="address.address_line_1"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.address_line_1]"/>
                    </div>

                    <div class="w-full">
                        <div class="flex justify-between">
                            <label :for="names.address_line_2" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.address_line_2') }}
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.address_line_2"
                            :id="names.address_line_2"
                            v-model="address.address_line_2"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.address_line_2]"/>
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4 mb-2">
                    <div>
                        <div class="flex justify-between">
                            <label :for="names.postal" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.postal') }} <span class="text-red-400">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.postal"
                            :id="names.postal"
                            v-model="address.postal"
                            autocomplete="off"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.postal]"/>
                    </div>
                    <div>
                        <div class="flex justify-between">
                            <label for="city" class="mb-2 block text-sm font-semibold text-slate-700">
                                {{ label('site.address.city') }} <span class="text-red-400">*</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            :name="names.city"
                            :id="names.city"
                            v-model="address.city"
                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        />
                        <Errors :errors="errors[names.city]"/>
                    </div>

                    <slot name="buttons"></slot>
                </div>

            </div>
        </div>
    </div>
</template>
